import {
  CustomerQuoteProcessSteps,
  CustomerQuoteProcessStep,
  CustomerQuoteProcessStepNames,
  StepStatus,
  type ActionType,
  type AppUser,
  type CustomerQuoteProcessStatus,
  type CustomerQuoteProcessStepEnum,
  type LoanApplication,
} from '@/types';
import { assign, createMachine } from 'xstate';
import { createLoanApp } from './builder';
import { LoanType } from './const';
import { SECTION_STATES_DEFAULT } from './sectionStatesUtil';

import type { SectionStates, SectionStateType } from '@/types/SectionState';

export interface CustomerQuoteMachineContext {
  loanApp: LoanApplication;
  sectionStates: SectionStates;
  from?: string;
  authorisedUser: Record<string, string>;
  status: CustomerQuoteProcessStatus;
  current: CustomerQuoteProcessStepEnum;
}

export const initCustomerQuoteMachine = (params?: {
  actionType: ActionType;
  userType: AppUser;
}): CustomerQuoteMachineContext => ({
  loanApp: createLoanApp(params),
  sectionStates: { ...SECTION_STATES_DEFAULT },
  authorisedUser: {},
  status: Object.fromEntries(
    CustomerQuoteProcessStepNames.map((step) => [
      step,
      step === CustomerQuoteProcessStep.IncomingProperties ? StepStatus.Ready : StepStatus.Incomplete,
    ]),
  ) as CustomerQuoteProcessStatus,
  current: CustomerQuoteProcessStep.IncomingProperties,
});

export type CustomerQuoteMachineEvent =
  | { type: 'NEXT'; loanApp: LoanApplication }
  | { type: 'UPDATE'; loanApp: LoanApplication }
  | { type: 'BACK' }
  | { type: 'READY' }
  | { type: 'GOTO'; step: CustomerQuoteProcessStepEnum }
  | { type: 'RELOAD'; state: CustomerQuoteMachineContext }
  | { type: 'RESTART' }
  | { type: 'UPDATE_SECTION_STATE'; sectionKey: keyof SectionStates; value: SectionStateType }
  | { type: 'UPDATE_ALL_SECTION_STATES'; value: SectionStateType };

export const loanAppCustomerQuoteMachine = createMachine<CustomerQuoteMachineContext, CustomerQuoteMachineEvent>(
  {
    id: 'loanApplicationCustomerQuote',
    predictableActionArguments: true,
    schema: {
      context: {} as CustomerQuoteMachineContext,
    },
    context: initCustomerQuoteMachine(),
    initial: 'start',
    states: {
      start: {
        on: {
          NEXT: {
            target: 'gotoReady',
          },
        },
      },
      // [CustomerQuoteProcessStep.StartQuote]: {
      //   on: {
      //     NEXT: [
      //       {
      //         target: CustomerQuoteProcessStep.IncomingProperties,
      //         actions: 'saveData',
      //       },
      //     ],
      //   },
      // },
      [CustomerQuoteProcessStep.IncomingProperties]: {
        on: {
          NEXT: {
            target: CustomerQuoteProcessStep.OutgoingProperties,
            actions: 'saveData',
          },
        },
      },
      [CustomerQuoteProcessStep.OutgoingProperties]: {
        on: {
          NEXT: {
            target: CustomerQuoteProcessStep.LoanDetails,
            actions: 'saveData',
          },
        },
      },
      [CustomerQuoteProcessStep.LoanDetails]: {
        on: {
          NEXT: {
            target: CustomerQuoteProcessStep.ApplicantDetails,
            actions: 'saveData',
          },
        },
      },
      [CustomerQuoteProcessStep.ApplicantDetails]: {
        on: {
          NEXT: {
            target: CustomerQuoteProcessStep.PropertyOwnership,
            actions: 'saveData',
          },
        },
      },
      [CustomerQuoteProcessStep.PropertyOwnership]: {
        on: {
          NEXT: {
            target: 'checkDebt',
            actions: 'saveData',
          },
        },
      },
      checkDebt: {
        always: [
          {
            target: CustomerQuoteProcessStep.Income,
            cond: 'showIncomeExpenses',
          },
          {
            target: CustomerQuoteProcessStep.Assets,
            cond: 'showAssets',
          },
          {
            target: CustomerQuoteProcessStep.VerifyIdentity,
          },
        ],
      },
      [CustomerQuoteProcessStep.Income]: {
        on: {
          NEXT: {
            target: CustomerQuoteProcessStep.Expenses,
            actions: 'saveData',
          },
        },
      },
      [CustomerQuoteProcessStep.Expenses]: {
        on: {
          NEXT: {
            target: CustomerQuoteProcessStep.Assets,
            actions: 'saveData',
          },
        },
      },
      [CustomerQuoteProcessStep.Assets]: {
        on: {
          NEXT: {
            target: CustomerQuoteProcessStep.VerifyIdentity,
            actions: 'saveData',
          },
        },
      },
      [CustomerQuoteProcessStep.VerifyIdentity]: {
        on: {
          NEXT: {
            target: CustomerQuoteProcessStep.SubmitCustomerQuote,
            actions: 'saveData',
          },
        },
      },
      [CustomerQuoteProcessStep.SubmitCustomerQuote]: {
        on: {
          NEXT: {
            target: CustomerQuoteProcessStep.Finished,
            actions: 'saveData',
          },
        },
      },
      [CustomerQuoteProcessStep.Finished]: {
        on: {
          NEXT: {
            target: CustomerQuoteProcessStep.Finished,
            actions: 'resetQuoteApplication',
          },
        },
      },
      prevStage: {
        always: [...CustomerQuoteProcessStepNames]
          .sort((a, b) => CustomerQuoteProcessSteps[b].order - CustomerQuoteProcessSteps[a].order)
          .map((step) => ({
            target: step,
            cond: (ctx) =>
              CustomerQuoteProcessSteps[ctx.current]?.order > CustomerQuoteProcessSteps[step].order &&
              ctx.status[step] === StepStatus.Completed,
          })),
      },
      gotoReady: {
        always: CustomerQuoteProcessStepNames.map((step) => ({
          target: step,
          cond: (ctx) => ctx.status[step] === StepStatus.Ready,
        })),
      },
    },
    on: {
      BACK: {
        target: 'prevStage',
        cond: (ctx) => ctx.current !== CustomerQuoteProcessStep.IncomingProperties,
      },
      READY: {
        target: 'gotoReady',
      },
      UPDATE: {
        actions: 'saveData',
      },
      GOTO: CustomerQuoteProcessStepNames.map((step) => ({
        target: step,
        cond: (ctx, evt) => evt.step === step,
      })),
      RELOAD: {
        target: 'gotoReady',
        actions: 'updateContext',
      },
      RESTART: {
        target: CustomerQuoteProcessStep.IncomingProperties,
        actions: 'resetQuoteApplication',
      },
      UPDATE_SECTION_STATE: {
        actions: 'updateSectionState',
      },
      UPDATE_ALL_SECTION_STATES: {
        actions: 'updateAllSectionStates',
      },
    },
  },
  {
    actions: {
      saveData: assign((context, event) => {
        return event.type === 'NEXT' || event.type === 'UPDATE'
          ? {
              loanApp: event.loanApp,
              sectionStates: context.sectionStates,
              status: Object.fromEntries(
                Object.entries(context.status).map(([step, status]) => [
                  step,
                  step === context.current
                    ? StepStatus.Completed
                    : CustomerQuoteProcessSteps[step as CustomerQuoteProcessStepEnum].order <
                      CustomerQuoteProcessSteps[context.current].order
                    ? status
                    : StepStatus.Incomplete,
                ]),
              ) as CustomerQuoteProcessStatus,
            }
          : {};
      }),
      updateContext: assign((context, event) => (event.type === 'RELOAD' ? event.state : {})),
      resetQuoteApplication: assign(() =>
        initCustomerQuoteMachine({
          userType: 'Primary',
          actionType: 'createNew',
        }),
      ),
      updateSectionState: assign((context, event) => {
        return event.type === 'UPDATE_SECTION_STATE'
          ? {
              sectionStates: {
                ...context.sectionStates,
                [event.sectionKey]: event.value,
              },
            }
          : {};
      }),
      updateAllSectionStates: assign((context, event) => {
        if (event.type === 'UPDATE_ALL_SECTION_STATES') {
          const updatedStates = Object.keys(context.sectionStates).reduce((acc, key) => {
            acc[key as keyof SectionStates] = event.value;
            return acc;
          }, {} as SectionStates);
          return {
            sectionStates: updatedStates,
          };
        }
        return {};
      }),
    },
    guards: {
      hasEndDebt: (ctx) => ctx.loanApp.endDebt > 0,
      showIncomeExpenses: (ctx) => {
        return (
          (ctx.loanApp.endDebt > 0 || (ctx.from ? ctx.from === 'crm' : false)) &&
          ctx.loanApp.loanType !== LoanType.SINGLE_SECURITY
        );
      },
      showAssets: (ctx) => {
        return ctx.loanApp.loanType === LoanType.SINGLE_SECURITY;
      },
    },
  },
);
